/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * 
 * LoggerService
 * Camada centralizada de registro de logs com níveis (DEBUG, INFO, WARN, ERROR)
 * e sanitização estrita de dados sensíveis (CPF, Tokens, API Keys, Pix, Dados Bancários).
 */

import {
  ObservabilityContext,
  ObservabilityEvent,
  ObservabilityEventInput,
  ObservabilityLevel,
  LatencyTimer,
  LatencyMeasurement,
} from "../../types/observability";

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  SILENT = 4,
}

// Em produção, DEBUG é desativado por padrão
const isProduction =
  typeof process !== "undefined" &&
  process.env &&
  (process.env.NODE_ENV === "production" || process.env.VITE_APP_ENV === "production");

let currentLogLevel: LogLevel = isProduction ? LogLevel.INFO : LogLevel.DEBUG;

/**
 * Sanitiza e mascara dados sensíveis para evitar vazamento em logs e eventos de observabilidade.
 */
export function sanitizeSensitiveData(input: any): any {
  if (input === null || input === undefined) return input;

  if (typeof input === "string") {
    let sanitized = input;

    // 1. Mascara CPF (ex: 123.456.789-00 ou 12345678900)
    sanitized = sanitized.replace(/\b(\d{3})\.?(\d{3})\.?(\d{3})-?(\d{2})\b/g, "$1.***.***-$4");

    // 2. Mascara tokens Bearer
    sanitized = sanitized.replace(/Bearer\s+[A-Za-z0-9\-_.]+/gi, "Bearer [REDACTED_TOKEN]");

    // 3. Mascara API Keys comuns (Asaas, Stripe, Mercado Pago)
    sanitized = sanitized.replace(/\$aact_[A-Za-z0-9\-_]{15,}/g, "$aact_***[REDACTED]");
    sanitized = sanitized.replace(/APP_USR-[A-Za-z0-9\-_]{15,}/g, "APP_USR-***[REDACTED]");
    sanitized = sanitized.replace(/sk_live_[A-Za-z0-9]{15,}/g, "sk_live_***[REDACTED]");
    sanitized = sanitized.replace(/sk_test_[A-Za-z0-9]{15,}/g, "sk_test_***[REDACTED]");

    // 4. Mascara parâmetros chave-valor em strings (password=, token=, apiKey=, authorization=, etc.)
    sanitized = sanitized.replace(
      /(api_key|apiKey|token|secret|password|senha|authorization|private_key|privateKey)=([^\s&]+)/gi,
      (match, p1, p2) => {
        if (p2.includes("[REDACTED]")) {
          return `${p1}=[REDACTED]`;
        }
        return `${p1}=[REDACTED]`;
      }
    );

    // 5. Mascara credenciais embutidas em connection strings e URLs com userinfo (ex: postgresql://user:pass@host:port/db)
    sanitized = sanitized.replace(/([a-zA-Z][a-zA-Z0-9+.-]*:\/\/)([^:\s\/@]*):([^@\s\/]+)@/g, "$1$2:[REDACTED]@");

    // 6. Mascara Pix Copia e Cola longo (EMV QR Code / Payload)
    if (sanitized.length > 80 && (sanitized.includes("00020126") || sanitized.includes("br.gov.bcb.pix"))) {
      sanitized = `${sanitized.substring(0, 15)}...[PIX_PAYLOAD_REDACTED]...${sanitized.substring(sanitized.length - 8)}`;
    }

    return sanitized;
  }

  if (Array.isArray(input)) {
    return input.map((item) => sanitizeSensitiveData(item));
  }

  if (typeof input === "object") {
    const output: Record<string, any> = {};
    for (const [key, value] of Object.entries(input)) {
      const lowerKey = key.toLowerCase();

      // Campos estritamente confidenciais por nome de chave
      if (
        lowerKey.includes("password") ||
        lowerKey.includes("senha") ||
        lowerKey.includes("secret") ||
        lowerKey.includes("apikey") ||
        lowerKey.includes("api_key") ||
        lowerKey.includes("accesstoken") ||
        lowerKey.includes("access_token") ||
        lowerKey.includes("token") ||
        lowerKey.includes("authorization") ||
        lowerKey.includes("private_key") ||
        lowerKey.includes("privatekey") ||
        lowerKey.includes("credit_card") ||
        lowerKey.includes("creditcard") ||
        lowerKey.includes("card_number") ||
        lowerKey.includes("cardnumber") ||
        lowerKey.includes("cvv") ||
        lowerKey.includes("cvc")
      ) {
        output[key] = "[REDACTED_CREDENTIAL]";
      } else if (lowerKey.includes("cpf") && typeof value === "string") {
        output[key] = value.replace(/\b(\d{3})\.?(\d{3})\.?(\d{3})-?(\d{2})\b/g, "$1.***.***-$4");
      } else if (
        (lowerKey.includes("pixcopiaecola") || lowerKey.includes("copiaecola") || lowerKey.includes("payloadpix")) &&
        typeof value === "string" &&
        value.length > 50
      ) {
        output[key] = `${value.substring(0, 15)}...[PIX_PAYLOAD_REDACTED]...${value.substring(value.length - 8)}`;
      } else if (lowerKey.includes("bank") || lowerKey.includes("conta") || lowerKey.includes("agencia")) {
        output[key] = "[REDACTED_BANK_INFO]";
      } else {
        output[key] = sanitizeSensitiveData(value);
      }
    }
    return output;
  }

  return input;
}

/**
 * Cria um timer de latência de alta precisão reutilizável.
 * Retorna uma função que ao ser executada retorna o tempo decorrido em milissegundos.
 */
export function createLatencyTimer(): LatencyTimer {
  const start = typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();

  return (): number => {
    const end = typeof performance !== "undefined" && typeof performance.now === "function"
      ? performance.now()
      : Date.now();
    const elapsed = end - start;
    return Math.max(0, Math.round(elapsed * 100) / 100);
  };
}

/**
 * Mede a latência de uma função assíncrona retornando o resultado e durationMs.
 */
export async function measureLatencyAsync<T>(fn: () => Promise<T>): Promise<LatencyMeasurement<T>> {
  const timer = createLatencyTimer();
  const result = await fn();
  const durationMs = timer();
  return { result, durationMs };
}

/**
 * Mede a latência de uma função síncrona retornando o resultado e durationMs.
 */
export function measureLatencySync<T>(fn: () => T): LatencyMeasurement<T> {
  const timer = createLatencyTimer();
  const result = fn();
  const durationMs = timer();
  return { result, durationMs };
}

function mapObservabilityLevelToLogLevel(level: ObservabilityLevel): LogLevel {
  switch (level) {
    case "debug": return LogLevel.DEBUG;
    case "info": return LogLevel.INFO;
    case "warn": return LogLevel.WARN;
    case "error": return LogLevel.ERROR;
    default: return LogLevel.INFO;
  }
}

export class LoggerService {
  static setLogLevel(level: LogLevel): void {
    currentLogLevel = level;
  }

  static getLogLevel(): LogLevel {
    return currentLogLevel;
  }

  static debug(message: string, ...args: any[]): void {
    if (currentLogLevel <= LogLevel.DEBUG) {
      const safeMessage = sanitizeSensitiveData(message);
      console.log(`[DEBUG] ${safeMessage}`, ...args.map(sanitizeSensitiveData));
    }
  }

  static info(message: string, ...args: any[]): void {
    if (currentLogLevel <= LogLevel.INFO) {
      const safeMessage = sanitizeSensitiveData(message);
      console.info(`[INFO] ${safeMessage}`, ...args.map(sanitizeSensitiveData));
    }
  }

  static warn(message: string, ...args: any[]): void {
    if (currentLogLevel <= LogLevel.WARN) {
      const safeMessage = sanitizeSensitiveData(message);
      console.warn(`[WARN] ${safeMessage}`, ...args.map(sanitizeSensitiveData));
    }
  }

  static error(message: string, ...args: any[]): void {
    if (currentLogLevel <= LogLevel.ERROR) {
      const safeMessage = sanitizeSensitiveData(message);
      console.error(`[ERROR] ${safeMessage}`, ...args.map(sanitizeSensitiveData));
    }
  }

  /**
   * Helper para sanitizar objetos antes de logar
   */
  static sanitize<T>(obj: T): T {
    return sanitizeSensitiveData(obj);
  }

  /**
   * Inicia um cronômetro de latência reutilizável.
   */
  static startTimer(): LatencyTimer {
    return createLatencyTimer();
  }

  /**
   * Registra um evento estruturado de observabilidade com sanitização total de dados.
   * Não força geração de requestId caso não seja informado.
   */
  static recordEvent(input: ObservabilityEventInput): ObservabilityEvent {
    let level: ObservabilityLevel = input.level || "info";
    const statusCode = typeof input.statusCode === "number"
      ? input.statusCode
      : (typeof (input as any).status === "number" ? (input as any).status : undefined);

    // Regra canônica: status 2xx e 3xx (incluindo 304 Not Modified) NUNCA são error nem warn
    if (typeof statusCode === "number") {
      if (statusCode >= 200 && statusCode < 400) {
        level = "info";
      } else if (statusCode >= 400 && statusCode < 500) {
        level = "warn";
      } else if (statusCode >= 500) {
        level = "error";
      }
    }

    const timestamp = input.timestamp || new Date().toISOString();

    const rawEvent: ObservabilityEvent = {
      timestamp,
      level,
      event: input.event,
    };

    if (input.requestId) rawEvent.requestId = input.requestId;
    if (input.correlationId) rawEvent.correlationId = input.correlationId;
    if (input.service) rawEvent.service = input.service;
    if (input.module) rawEvent.module = input.module;
    if (input.operation) rawEvent.operation = input.operation;
    if (input.method) rawEvent.method = input.method;
    if (input.path) rawEvent.path = input.path;
    if (typeof input.statusCode === "number") rawEvent.statusCode = input.statusCode;
    if (typeof input.durationMs === "number") rawEvent.durationMs = input.durationMs;
    if (input.status !== undefined) rawEvent.status = input.status;
    if (input.errorCode) rawEvent.errorCode = input.errorCode;
    if (input.context && Object.keys(input.context).length > 0) {
      rawEvent.context = input.context;
    }

    const sanitizedEvent: ObservabilityEvent = sanitizeSensitiveData(rawEvent);
    const targetLogLevel = mapObservabilityLevelToLogLevel(level);

    if (currentLogLevel <= targetLogLevel) {
      // Redução de logs de requisições normais no console (Item 5):
      // Requisições 2xx e 3xx normais continuam registradas e enviadas ao agregador e store,
      // mas não imprimem o objeto de observabilidade completo no terminal se fora do modo DEBUG.
      const isNormalHttpRequest = sanitizedEvent.event === "http.request" && (level === "info" || level === "debug");

      if (isNormalHttpRequest && currentLogLevel > LogLevel.DEBUG) {
        // Mantém o console limpo em operação normal
      } else {
        switch (level) {
          case "debug":
            console.log(`[OBSERVABILITY] [${sanitizedEvent.event}]`, sanitizedEvent);
            break;
          case "info":
            console.info(`[OBSERVABILITY] [${sanitizedEvent.event}]`, sanitizedEvent);
            break;
          case "warn":
            console.warn(`[OBSERVABILITY] [${sanitizedEvent.event}]`, sanitizedEvent);
            break;
          case "error":
            console.error(`[OBSERVABILITY] [${sanitizedEvent.event}]`, sanitizedEvent);
            break;
        }
      }
    }

    return sanitizedEvent;
  }

  /**
   * Alias semântico para recordEvent.
   */
  static logEvent(input: ObservabilityEventInput): ObservabilityEvent {
    return LoggerService.recordEvent(input);
  }

  /**
   * Executa operação assíncrona com medição de latência automática e registro de evento estruturado.
   */
  static async measureAsync<T>(
    eventInputOrName: string | ObservabilityEventInput,
    fn: () => Promise<T>,
    additionalContext?: ObservabilityContext
  ): Promise<T> {
    const baseInput: ObservabilityEventInput =
      typeof eventInputOrName === "string" ? { event: eventInputOrName } : { ...eventInputOrName };

    if (additionalContext) {
      baseInput.context = { ...(baseInput.context || {}), ...additionalContext };
      if (!baseInput.requestId && additionalContext.requestId) {
        baseInput.requestId = additionalContext.requestId;
      }
      if (!baseInput.correlationId && additionalContext.correlationId) {
        baseInput.correlationId = additionalContext.correlationId;
      }
      if (!baseInput.service && additionalContext.service) {
        baseInput.service = additionalContext.service;
      }
      if (!baseInput.module && additionalContext.module) {
        baseInput.module = additionalContext.module;
      }
      if (!baseInput.operation && additionalContext.operation) {
        baseInput.operation = additionalContext.operation;
      }
    }

    const timer = createLatencyTimer();
    try {
      const result = await fn();
      const durationMs = timer();

      LoggerService.recordEvent({
        ...baseInput,
        level: baseInput.level || "info",
        status: baseInput.status || "success",
        durationMs,
      });

      return result;
    } catch (err: any) {
      const durationMs = timer();
      LoggerService.recordEvent({
        ...baseInput,
        level: "error",
        status: "error",
        errorCode: err?.code || err?.name || "OPERATION_ERROR",
        durationMs,
        context: {
          ...(baseInput.context || {}),
          errorMessage: err?.message || String(err),
        },
      });
      throw err;
    }
  }

  /**
   * Executa operação síncrona com medição de latência automática e registro de evento estruturado.
   */
  static measureSync<T>(
    eventInputOrName: string | ObservabilityEventInput,
    fn: () => T,
    additionalContext?: ObservabilityContext
  ): T {
    const baseInput: ObservabilityEventInput =
      typeof eventInputOrName === "string" ? { event: eventInputOrName } : { ...eventInputOrName };

    if (additionalContext) {
      baseInput.context = { ...(baseInput.context || {}), ...additionalContext };
      if (!baseInput.requestId && additionalContext.requestId) {
        baseInput.requestId = additionalContext.requestId;
      }
      if (!baseInput.correlationId && additionalContext.correlationId) {
        baseInput.correlationId = additionalContext.correlationId;
      }
      if (!baseInput.service && additionalContext.service) {
        baseInput.service = additionalContext.service;
      }
      if (!baseInput.module && additionalContext.module) {
        baseInput.module = additionalContext.module;
      }
      if (!baseInput.operation && additionalContext.operation) {
        baseInput.operation = additionalContext.operation;
      }
    }

    const timer = createLatencyTimer();
    try {
      const result = fn();
      const durationMs = timer();

      LoggerService.recordEvent({
        ...baseInput,
        level: baseInput.level || "info",
        status: baseInput.status || "success",
        durationMs,
      });

      return result;
    } catch (err: any) {
      const durationMs = timer();
      LoggerService.recordEvent({
        ...baseInput,
        level: "error",
        status: "error",
        errorCode: err?.code || err?.name || "OPERATION_ERROR",
        durationMs,
        context: {
          ...(baseInput.context || {}),
          errorMessage: err?.message || String(err),
        },
      });
      throw err;
    }
  }
}

